import { type FC, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { Grid, Stack, Tooltip } from '@mui/material';
import {
  type GridColDef,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { BadgeCheckIcon, CheckIcon, XIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  GetPropertyTransactionsQuery,
  TransactionsListRowFragment,
} from '@graphql/__generated__/graphql';
import { PropertyCell } from '@modules/_shared/components/data-grid/PropertyCell';
import {
  RaDataGrid,
  type RaDataGridProps,
} from '@modules/_shared/components/data-grid/RaDataGrid';
import { TeamInfo } from '@modules/_shared/components/data-grid/TeamInfo';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatDateNumeric } from '@modules/_shared/utils/formatting';
import { translateAppraisalReason } from '@modules/_shared/utils/lead-labels';
import { GET_TRANSACTION_VALUATIONS } from '@modules/transactions/transactionQueries';
import {
  type IntlLocale,
  type Translate,
  useLocale,
} from '@shared/hooks/locale';
import { type Currency, getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

type TransactionsListProps = {
  transactions: TransactionsListRowFragment[] | undefined;
  loading: boolean;
  extraDataGridProps?: Omit<RaDataGridProps, 'loading' | 'rows' | 'columns'>;
  displayPropertyInfo?: boolean;
  showReviews?: boolean;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
};

export const TRANSATIONS_LIST_ROW_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionsListRow on property_transactions {
    id
    is_published
    signature_date
    estimated_signature_date
    payment_date
    purchase_price
    seller_broker {
      id
      ...UserInfo_user
    }
    buyer_broker {
      id
      ...UserInfo_user
    }
    seller_broker_team {
      id
      ...TeamInfo_team
    }
    buyer_broker_team {
      id
      ...TeamInfo_team
    }
    creator {
      id
      ...UserInfo_user
    }
    property {
      ...PropertyCell_property
      living_surface
      built_surface
      land_surface
      number_of_rooms
      number_of_bedrooms
      property_type {
        id
        label
      }
    }
    commissions {
      id
      is_paid
      amount
    }
    lot {
      id
      currency
    }
    sellers(limit: 1) {
      user {
        id
        ...UserInfo_user
      }
    }
    buyers(limit: 1) {
      user {
        id
        ...UserInfo_user
      }
    }
    commissions_aggregate(where: { type: { _eq: "total" } }) {
      aggregate {
        sum {
          amount
        }
      }
    }
    my_commissions: commissions_aggregate(
      where: { type: { _eq: "split" }, beneficiary_team_id: { _in: $team_ids } }
    ) {
      aggregate {
        sum {
          amount
        }
      }
    }
    transaction_valuations_aggregate @include(if: $includeValuations) {
      aggregate {
        count
      }
    }
  }
`);

const formatAmountWithLotCurrency = (
  amount: number | null | undefined,
  locale: IntlLocale,
  transaction: TransactionsListRowFragment,
  // The same currency the group footer uses. A row whose property is hidden
  // has no country code, and falling back to CH would print francs next to a
  // footer in euros, in one column.
  fallbackCurrency: Currency,
) => {
  const currency = (transaction.lot?.currency ??
    (transaction.property?.country_code != null
      ? getCurrencyByCountryCode(transaction.property.country_code)
      : fallbackCurrency)) as Currency;

  return formatPrice(amount, locale, currency);
};

type ValuationsCellProps = {
  transactionId: string;
  count: number;
};

const ValuationsCell: FC<ValuationsCellProps> = ({ transactionId, count }) => {
  const { t } = useLocale();
  const [loadValuations, { data, loading }] = useLazyQuery(
    GET_TRANSACTION_VALUATIONS,
    { notifyOnNetworkStatusChange: true },
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
    if (!data && !loading) {
      loadValuations({ variables: { transactionId } });
    }
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  if (count === 0) {
    return '-';
  }

  return (
    <Tooltip
      title={
        loading ? (
          t('Loading...')
        ) : (
          <Stack>
            {data?.transaction_valuations?.map(valuation => (
              <div key={valuation.lead_id}>
                <strong>
                  {[valuation.first_name, valuation.last_name]
                    .filter(Boolean)
                    .join(' ')}{' '}
                </strong>{' '}
                -{' '}
                {translateAppraisalReason(t, valuation.appraisal_reason ?? '')}
              </div>
            )) ?? <div>{t('No valuations found')}</div>}
          </Stack>
        )
      }
      open={tooltipOpen}
      onOpen={handleTooltipOpen}
      onClose={handleTooltipClose}
      arrow
    >
      <span>{t('{{count}} valuations', { count })}</span>
    </Tooltip>
  );
};

const getColumns = (
  t: Translate,
  locale: IntlLocale,
  displayPropertyInfo: boolean,
  tenantCountryCode: string | undefined,
  showReviews: boolean,
  canEditSettings: boolean | undefined,
): GridColDef<TransactionsListRowFragment>[] => {
  const columns: GridColDef<TransactionsListRowFragment>[] = [];
  const defaultCurrency = getCurrencyByCountryCode(tenantCountryCode ?? 'CH');

  if (displayPropertyInfo) {
    columns.push(
      {
        field:
          'property.postcode,property.locality,property.route,property.street_number',
        headerName: t('Listing'),
        width: 200,
        sortable: false,
        renderCell: ({ row, id }) =>
          id !== 'auto-generated-group-footer-root' && (
            <PropertyCell property={row.property} />
          ),
      },
      {
        field: 'property',
        headerName: t('Property Information'),
        width: 190,
        display: 'flex',
        sortable: false,
        renderCell: ({ row, id }) =>
          id !== 'auto-generated-group-footer-root' && (
            <div>
              {/* `property` is non-null in the schema, but Hasura returns null
                  when the agent may see the transaction and not its property. */}
              <div>
                {row.property?.property_type?.label ?? <i>{t('No type')}</i>}
              </div>
              <div>
                {(row.property?.country_code ?? tenantCountryCode) === 'ES'
                  ? t(
                      '{{number_of_bedrooms}} bedrooms ﹒ {{built_surface}} m²',
                      {
                        number_of_bedrooms:
                          row.property?.number_of_bedrooms ?? '-',
                        built_surface: row.property?.built_surface ?? '-',
                        count: row.property?.number_of_bedrooms ?? 0,
                      },
                    )
                  : t('{{number_of_rooms}} rooms ﹒ {{living_surface}} m²', {
                      number_of_rooms: row.property?.number_of_rooms ?? '-',
                      living_surface: row.property?.living_surface ?? '-',
                      count: row.property?.number_of_rooms ?? 0,
                    })}
              </div>
            </div>
          ),
      },
    );
  }

  columns.push(
    {
      field: 'signature_date',
      headerName: t('Signature date'),
      width: 150,
      renderCell: ({ row }) => {
        if (row.signature_date) {
          return formatDateNumeric(new Date(row.signature_date), locale);
        } else if (row.estimated_signature_date) {
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
          }).format(new Date(row.estimated_signature_date));
        }
        return '-';
      },
    },
    {
      field: 'is_published',
      headerName: t('Published'),
      width: 100,
      display: 'flex',
      renderCell: ({ row, id }) => {
        if (id === 'auto-generated-group-footer-root') {
          return null;
        }
        return row.is_published ? <CheckIcon size={18} /> : <XIcon size={18} />;
      },
    },
  );

  columns.push(
    {
      field: 'purchase_price',
      headerName: t('Price'),
      width: 140,
      display: 'flex',
      align: 'right',
      type: 'number',
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row, defaultCurrency),
    },
    {
      field: 'seller_broker.last_name,seller_broker.first_name',
      headerName: t('Seller agent'),
      display: 'flex',
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.seller_broker && <UserInfo user={row.seller_broker} />,
    },
    {
      field: 'seller_broker_team.name',
      headerName: t('Seller agent team'),
      display: 'flex',
      width: 250,
      renderCell: ({ row }) =>
        row.seller_broker_team && <TeamInfo team={row.seller_broker_team} />,
    },
    {
      field: 'buyer_broker.last_name,buyer_broker.first_name',
      headerName: t('Buyer agent'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyer_broker && <UserInfo user={row.buyer_broker} />,
    },
    {
      field: 'buyer_broker_team.name',
      headerName: t('Buyer agent team'),
      display: 'flex',
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyer_broker_team && <TeamInfo team={row.buyer_broker_team} />,
    },
    {
      field: 'creator.last_name,creator.first_name',
      headerName: t('Uploaded by'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) => row.creator && <UserInfo user={row.creator} />,
    },
    {
      field: 'buyers.user.last_name,buyers.user.first_name',
      headerName: t('Buyer'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.buyers?.[0] && <UserInfo user={row.buyers[0].user} />,
    },
    {
      field: 'sellers.user.last_name,sellers.user.first_name',
      headerName: t('Seller'),
      width: 250,
      sortable: false,
      renderCell: ({ row }) =>
        row.sellers?.[0] && <UserInfo user={row.sellers[0].user} />,
    },
  );

  if (canEditSettings) {
    columns.push({
      field: 'transaction_valuations_aggregate.count',
      headerName: t('Valuations'),
      width: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const count =
          row.transaction_valuations_aggregate?.aggregate?.count ?? 0;

        return <ValuationsCell transactionId={row.id} count={count} />;
      },
    });
  }

  columns.push({
    field: 'payment_date',
    headerName: t('Payment Date'),
    width: 120,
    renderCell: ({ row }) =>
      row.payment_date
        ? formatDateNumeric(new Date(row.payment_date), locale)
        : '-',
  });

  if (showReviews) {
    columns.push({
      field: 'reviews',
      sortable: false,
      headerName: t('Reviews'),
      width: 80,
      display: 'flex',
      renderCell: ({ row, id }) => {
        if (id === 'auto-generated-group-footer-root') {
          return null;
        }

        const rowWithReviews =
          row as GetPropertyTransactionsQuery['property_transactions'][number];

        const hasBuyerReview = rowWithReviews.reviews.some(r => r.is_buyer);
        const hasSellerReview = rowWithReviews.reviews.some(r => !r.is_buyer);

        let tooltipText = t('No reviews');

        if (hasBuyerReview && hasSellerReview) {
          tooltipText = t('Buyer and seller reviews');
        } else if (hasBuyerReview) {
          tooltipText = t('Buyer review');
        } else if (hasSellerReview) {
          tooltipText = t('Seller review');
        }

        return (
          <Tooltip title={tooltipText}>
            <Grid container>
              <Grid sx={{ textAlign: 'center' }} size={6}>
                {hasSellerReview ? (
                  <BadgeCheckIcon
                    size={18}
                    color={'var(--mui-palette-success-main)'}
                  />
                ) : (
                  '-'
                )}
              </Grid>
              <Grid sx={{ textAlign: 'center' }} size={6}>
                {hasBuyerReview ? (
                  <BadgeCheckIcon
                    size={18}
                    color={'var(--mui-palette-success-main)'}
                  />
                ) : (
                  '-'
                )}
              </Grid>
            </Grid>
          </Tooltip>
        );
      },
    });
  }

  columns.push(
    {
      field: 'commissions_aggregate.sum',
      headerName: t('Rate'),
      width: 120,
      align: 'right',
      type: 'number',
      sortable: false,
      valueGetter: (_, row) =>
        row.purchase_price &&
        row.commissions_aggregate?.aggregate?.sum?.amount &&
        row.commissions_aggregate?.aggregate?.sum?.amount / row.purchase_price,
      valueFormatter: (value: number) =>
        value != null
          ? new Intl.NumberFormat(locale, {
              style: 'percent',
              maximumFractionDigits: 1,
            }).format(value)
          : '-',
    },
    {
      field: 'commissions_aggregate.sum.amount',
      headerName: t('Commission'),
      width: 120,
      align: 'right',
      type: 'number',
      display: 'flex',
      valueGetter: (_, row) =>
        row.commissions_aggregate?.aggregate?.sum?.amount ?? 0,
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row, defaultCurrency),
    },
    {
      field: 'my_commissions.aggregate.sum.amount',
      headerName: t('My Share'),
      width: 120,
      align: 'right',
      type: 'number',
      sortable: false,
      valueGetter: (_, row) => row.my_commissions?.aggregate?.sum?.amount ?? 0,
      renderCell: ({ row, value, id }) =>
        id === 'auto-generated-group-footer-root'
          ? formatPrice(value, locale, defaultCurrency, 0, '-')
          : formatAmountWithLotCurrency(value, locale, row, defaultCurrency),
    },
  );

  return columns;
};

export const TransactionsList: FC<TransactionsListProps> = ({
  transactions,
  loading,
  displayPropertyInfo = false,
  extraDataGridProps,
  showReviews = false,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
}) => {
  const { t, intlLocale } = useLocale();
  const { me } = useAppData();
  const columns = useMemo(
    () =>
      getColumns(
        t,
        intlLocale,
        displayPropertyInfo,
        me?.tenant.country_code,
        showReviews,
        me?.can_edit_settings ?? false,
      ),
    [
      t,
      intlLocale,
      displayPropertyInfo,
      me?.tenant.country_code,
      showReviews,
      me?.can_edit_settings,
    ],
  );

  return (
    <RaDataGrid
      loading={loading}
      rows={transactions ?? []}
      columns={columns}
      disableColumnSelector={false}
      disableColumnFilter
      checkboxSelection={checkboxSelection}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!!checkboxSelection}
      disableRowSelectionExcludeModel={!!checkboxSelection}
      onRowSelectionModelChange={onRowSelectionModelChange}
      {...extraDataGridProps}
    />
  );
};
